/**
 * Drop Zone (S2-T2.1).
 *
 * Accepts a single PDF via drag-drop, click-to-pick, or paste, reads its
 * bytes, and hands them up via `onFile`. Non-PDF input is rejected with a
 * message. v1 is single-document (the funnel previews one file); the engine
 * supports batch, but the funnel doesn't expose it.
 */

import { useRef, useState } from "react";

import styles from "./DropZone.module.css";

export interface LoadedPdf {
  name: string;
  bytes: Uint8Array;
}

/** Pure: first PDF in a file list, or null. Exported for unit testing. */
export function pickPdf(files: FileList | File[] | null | undefined): File | null {
  if (!files) return null;
  for (const file of Array.from(files)) {
    if (file.type === "application/pdf" || file.name.toLowerCase().endsWith(".pdf")) {
      return file;
    }
  }
  return null;
}

interface DropZoneProps {
  onFile: (pdf: LoadedPdf) => void;
  loadedName?: string | null;
  /** When provided and a file is loaded, shows a Remove control to clear it. */
  onClear?: () => void;
}

export function DropZone({ onFile, loadedName, onClear }: DropZoneProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function accept(files: FileList | File[] | null | undefined): Promise<void> {
    const pdf = pickPdf(files);
    if (!pdf) {
      setError("That doesn't look like a PDF. Drop or choose a .pdf file.");
      return;
    }
    setError(null);
    const bytes = new Uint8Array(await pdf.arrayBuffer());
    onFile({ name: pdf.name, bytes });
  }

  function openPicker(): void {
    inputRef.current?.click();
  }

  return (
    <section
      className={`${styles.dropzone} ${dragging ? styles.dragging : ""}`}
      role="button"
      tabIndex={0}
      aria-label="Drop a PDF to number it"
      onClick={openPicker}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          openPicker();
        }
      }}
      onDragOver={(e) => {
        e.preventDefault();
        setDragging(true);
      }}
      onDragLeave={() => setDragging(false)}
      onDrop={(e) => {
        e.preventDefault();
        setDragging(false);
        void accept(e.dataTransfer?.files);
      }}
      onPaste={(e) => void accept(e.clipboardData?.files)}
    >
      <input
        ref={inputRef}
        type="file"
        accept="application/pdf"
        hidden
        onChange={(e) => void accept(e.target.files)}
      />
      <div>
        {loadedName ? (
          <>
            <h2 className={styles.headline}>Loaded</h2>
            <p className={styles.loadedName}>{loadedName}</p>
            <p className={styles.sub}>Drop another PDF to replace it.</p>
            {onClear && (
              <button
                type="button"
                className={styles.clear}
                onClick={(e) => {
                  e.stopPropagation();
                  onClear();
                }}
              >
                Remove
              </button>
            )}
          </>
        ) : (
          <>
            <h2 className={styles.headline}>Drop a PDF to number it</h2>
            <p className={styles.sub}>or click to choose a file. It never leaves your browser.</p>
          </>
        )}
        {error && (
          <p className={styles.error} role="alert">
            {error}
          </p>
        )}
      </div>
    </section>
  );
}
